import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { auth, admin } from '../lib/api';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Badge } from '../components/ui/badge';
import { Skeleton } from '../components/ui/skeleton';
import { Alert, AlertDescription } from '../components/ui/alert';
import AdminHeader from '../components/AdminHeader';
import {
  ChevronLeft,
  Plus,
  Edit,
  Trash2,
  Save,
  X,
  Settings,
  CheckCircle,
  XCircle,
  GripVertical
} from 'lucide-react';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:8080';

export default function AdminEmailRules() {
  const navigate = useNavigate();
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  
  // Rules state
  const [rules, setRules] = useState([]);
  const [showEditor, setShowEditor] = useState(false);
  const [editingRule, setEditingRule] = useState(null);

  // Drag and drop state
  const [draggedRule, setDraggedRule] = useState(null);
  const [dragOverRule, setDragOverRule] = useState(null);

  // Folder state
  const [customFolders, setCustomFolders] = useState([]);

  // Form state
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    rule_text: '',
    priority: 0,
    enabled: true,
    actions: {
      move_to_folder: 'inbox',
      send_response: false,
      notify_admin: false
    }
  });

  // Define handleCreateRule before useEffect that uses it
  const handleCreateRule = useCallback(() => {
    setEditingRule(null);
    setFormData({
      name: '',
      description: '',
      rule_text: '',
      priority: rules.length,
      enabled: true,
      actions: {
        move_to_folder: 'inbox',
        send_response: false,
        notify_admin: false
      }
    });
    setShowEditor(true);
  }, [rules.length]);

  useEffect(() => {
    // Check if user is authenticated and is admin
    if (!auth.isAuthenticated()) {
      navigate('/login');
      return;
    }

    const userData = auth.getUser();
    if (!userData?.is_admin) {
      navigate('/dashboard');
      return;
    }

    setUser(userData);
    loadRules();
  }, [navigate]);

  // Handle create rule button click from AdminHeader
  useEffect(() => {
    const createBtn = document.getElementById('create-rule-btn');
    if (createBtn) {
      createBtn.addEventListener('click', handleCreateRule);
      return () => createBtn.removeEventListener('click', handleCreateRule);
    }
  }, [handleCreateRule]);

  // Load custom folders from localStorage
  useEffect(() => {
    const savedFolders = localStorage.getItem('customEmailFolders');
    if (savedFolders) {
      try {
        setCustomFolders(JSON.parse(savedFolders));
      } catch (e) {
        console.error('Error loading custom folders:', e);
      }
    }
  }, []);

  const loadRules = async () => {
    try {
      setLoading(true);
      const response = await admin.listEmailRules();
      setRules(response.data?.rules || []);
      setError('');
    } catch (err) {
      console.error('Error loading rules:', err);
      setError('Failed to load email rules');
      setRules([]);
    } finally {
      setLoading(false);
    }
  };

  // Drag and drop handlers
  const handleDragStart = (e, rule) => {
    setDraggedRule(rule);
    e.dataTransfer.effectAllowed = 'move';
  };

  const handleDragOver = (e, rule) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
    if (draggedRule && draggedRule.id !== rule.id) {
      setDragOverRule(rule);
    }
  };

  const handleDragLeave = () => {
    setDragOverRule(null);
  };

  const handleDrop = async (e, targetRule) => {
    e.preventDefault();

    if (!draggedRule || draggedRule.id === targetRule.id) {
      setDraggedRule(null);
      setDragOverRule(null);
      return;
    }

    // Reorder rules array
    const draggedIndex = rules.findIndex(r => r.id === draggedRule.id);
    const targetIndex = rules.findIndex(r => r.id === targetRule.id);

    const newRules = [...rules];
    newRules.splice(draggedIndex, 1);
    newRules.splice(targetIndex, 0, draggedRule);

    // Update priorities based on new order
    const updatedRules = newRules.map((rule, index) => ({
      ...rule,
      priority: index
    }));

    setRules(updatedRules);
    setDraggedRule(null);
    setDragOverRule(null);

    // Update priorities on server
    try {
      await Promise.all(
        updatedRules.map(rule =>
          admin.updateEmailRule(rule.id, { priority: rule.priority })
        )
      );
      setSuccess('Rule order updated successfully');
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      console.error('Error updating rule priorities:', err);
      setError('Failed to update rule order');
      loadRules(); // Reload to get correct order
    }
  };

  const handleDragEnd = () => {
    setDraggedRule(null);
    setDragOverRule(null);
  };

  const handleEditRule = (rule) => {
    setEditingRule(rule);
    setFormData({
      name: rule.name,
      description: rule.description,
      rule_text: rule.rule_text,
      priority: rule.priority,
      enabled: rule.enabled,
      actions: rule.actions
    });
    setShowEditor(true);
  };

  const handleSaveRule = async () => {
    try {
      if (!formData.name || !formData.rule_text) {
        setError('Name and rule text are required');
        return;
      }

      if (editingRule) {
        await admin.updateEmailRule(editingRule.id, formData);
        setSuccess('Rule updated successfully');
      } else {
        await admin.createEmailRule(formData);
        setSuccess('Rule created successfully');
      }

      setShowEditor(false);
      setEditingRule(null);
      loadRules();
      
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      console.error('Error saving rule:', err);
      setError('Failed to save rule');
    }
  };

  const handleDeleteRule = async (ruleId) => {
    if (!window.confirm('Are you sure you want to delete this rule?')) return;

    try {
      await admin.deleteEmailRule(ruleId);
      setSuccess('Rule deleted successfully');
      loadRules();
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      console.error('Error deleting rule:', err);
      setError('Failed to delete rule');
    }
  };

  const handleToggleEnabled = async (rule) => {
    try {
      await admin.updateEmailRule(rule.id, { enabled: !rule.enabled });
      loadRules();
    } catch (err) {
      console.error('Error toggling rule:', err);
      setError('Failed to update rule');
    }
  };

  // Define default folders
  const defaultFolders = [
    { id: 'inbox', label: 'Inbox' },
    { id: 'ai-processed', label: 'AI Processed' },
    { id: 'sent', label: 'Sent' },
    { id: 'spam', label: 'Spam' },
    { id: 'trash', label: 'Trash' }
  ];

  // Combine default and custom folders
  const allFolders = [...defaultFolders, ...customFolders];

  if (!user) {
    return null;
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-900 via-purple-900 to-gray-900">
      {/* Admin Header */}
      <AdminHeader />

      {/* Main Content */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {error && (
          <Alert variant="destructive" className="mb-6">
            <AlertDescription>{error}</AlertDescription>
          </Alert>
        )}

        {success && (
          <Alert className="mb-6 bg-green-900/50 border-green-500/20">
            <AlertDescription className="text-green-400">{success}</AlertDescription>
          </Alert>
        )}

        {showEditor ? (
          <Card className="bg-gray-900/50 border-purple-500/20 backdrop-blur-xl mb-6">
            <CardHeader>
              <CardTitle className="text-white">
                {editingRule ? 'Edit Rule' : 'Create New Rule'}
              </CardTitle>
              <CardDescription className="text-gray-400">
                Define natural language rules for AI email processing
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">
                  Rule Name *
                </label>
                <Input
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  placeholder="e.g., Support Request Handler"
                  className="bg-gray-800 border-gray-700 text-white"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">
                  Description
                </label>
                <Input
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  placeholder="Optional description for reference"
                  className="bg-gray-800 border-gray-700 text-white"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">
                  Rule Text (Natural Language) *
                </label>
                <Textarea
                  value={formData.rule_text}
                  onChange={(e) => setFormData({ ...formData, rule_text: e.target.value })}
                  placeholder="e.g., If this email is from a user who needs assistance, review the email, review the Cogsmith docs located at https://cogsmith.ai/docs, and if you can answer or help them with their issue, respond to the email."
                  rows={6}
                  className="bg-gray-800 border-gray-700 text-white"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">
                  Move to Folder
                </label>
                <select
                  value={formData.actions.move_to_folder}
                  onChange={(e) => setFormData({
                    ...formData,
                    actions: { ...formData.actions, move_to_folder: e.target.value }
                  })}
                  className="w-full px-3 py-2 bg-gray-800 border border-gray-700 rounded-md text-white"
                >
                  {allFolders.map(folder => (
                    <option key={folder.id} value={folder.id}>
                      {folder.label}
                    </option>
                  ))}
                </select>
                <p className="text-xs text-gray-500 mt-1">
                  Rule priority is determined by the order in the list below. Drag and drop rules to reorder.
                </p>
              </div>

              <div className="space-y-2">
                <label className="flex items-center space-x-2">
                  <input
                    type="checkbox"
                    checked={formData.actions.send_response}
                    onChange={(e) => setFormData({
                      ...formData,
                      actions: { ...formData.actions, send_response: e.target.checked }
                    })}
                    className="rounded border-gray-700 bg-gray-800 text-purple-600"
                  />
                  <span className="text-sm text-gray-300">Allow AI to respond (AI decides when to send)</span>
                </label>

                <label className="flex items-center space-x-2">
                  <input
                    type="checkbox"
                    checked={formData.actions.notify_admin}
                    onChange={(e) => setFormData({
                      ...formData,
                      actions: { ...formData.actions, notify_admin: e.target.checked }
                    })}
                    className="rounded border-gray-700 bg-gray-800 text-purple-600"
                  />
                  <span className="text-sm text-gray-300">Notify admin</span>
                </label>

                <label className="flex items-center space-x-2">
                  <input
                    type="checkbox"
                    checked={formData.enabled}
                    onChange={(e) => setFormData({ ...formData, enabled: e.target.checked })}
                    className="rounded border-gray-700 bg-gray-800 text-purple-600"
                  />
                  <span className="text-sm text-gray-300">Enabled</span>
                </label>
              </div>

              <div className="flex space-x-2 pt-4">
                <Button
                  onClick={handleSaveRule}
                  className="bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-700 hover:to-pink-700"
                >
                  <Save className="h-4 w-4 mr-2" />
                  Save Rule
                </Button>
                <Button
                  variant="outline"
                  onClick={() => {
                    setShowEditor(false);
                    setEditingRule(null);
                  }}
                  className="border-gray-700 text-gray-300 hover:bg-gray-800"
                >
                  <X className="h-4 w-4 mr-2" />
                  Cancel
                </Button>
              </div>
            </CardContent>
          </Card>
        ) : null}

        {/* Rules List */}
        <div className="space-y-4">
          {loading ? (
            Array.from({ length: 3 }).map((_, i) => (
              <Card key={i} className="bg-gray-900/50 border-purple-500/20 backdrop-blur-xl">
                <CardContent className="p-6">
                  <Skeleton className="h-6 w-1/3 mb-2" />
                  <Skeleton className="h-4 w-2/3" />
                </CardContent>
              </Card>
            ))
          ) : rules.length === 0 ? (
            <Card className="bg-gray-900/50 border-purple-500/20 backdrop-blur-xl">
              <CardContent className="p-12 text-center">
                <Settings className="h-16 w-16 mx-auto mb-4 text-gray-600" />
                <p className="text-gray-400 mb-4">No email rules configured yet</p>
                <Button
                  onClick={handleCreateRule}
                  className="bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-700 hover:to-pink-700"
                >
                  <Plus className="h-4 w-4 mr-2" />
                  Create Your First Rule
                </Button>
              </CardContent>
            </Card>
          ) : (
            rules.map((rule) => (
              <Card
                key={rule.id}
                className={`bg-gray-900/50 border-purple-500/20 backdrop-blur-xl transition-all ${
                  dragOverRule?.id === rule.id ? 'border-purple-500 border-2' : ''
                } ${draggedRule?.id === rule.id ? 'opacity-50' : ''}`}
                draggable
                onDragStart={(e) => handleDragStart(e, rule)}
                onDragOver={(e) => handleDragOver(e, rule)}
                onDragLeave={handleDragLeave}
                onDrop={(e) => handleDrop(e, rule)}
                onDragEnd={handleDragEnd}
              >
                <CardContent className="p-6">
                  <div className="flex items-start justify-between">
                    <div className="flex items-center space-x-3 flex-1">
                      <div className="cursor-move text-gray-500 hover:text-gray-300">
                        <GripVertical className="h-5 w-5" />
                      </div>
                      <div className="flex-1">
                        <div className="flex items-center space-x-2 mb-2">
                          <h3 className="text-lg font-semibold text-white">{rule.name}</h3>
                        <Badge
                          variant={rule.enabled ? 'default' : 'secondary'}
                          className={rule.enabled ? 'bg-green-600' : 'bg-gray-600'}
                        >
                          {rule.enabled ? 'Enabled' : 'Disabled'}
                        </Badge>
                      </div>
                      {rule.description && (
                        <p className="text-sm text-gray-400 mb-2">{rule.description}</p>
                      )}
                      <p className="text-sm text-gray-300 mb-3">{rule.rule_text}</p>
                      <div className="flex flex-wrap gap-2">
                        <Badge variant="outline" className="border-gray-700 text-gray-400">
                          Folder: {rule.actions.move_to_folder}
                        </Badge>
                        {rule.actions.send_response && (
                          <Badge variant="outline" className="border-blue-500/20 text-blue-400">
                            Auto-respond
                          </Badge>
                        )}
                        {rule.actions.notify_admin && (
                          <Badge variant="outline" className="border-yellow-500/20 text-yellow-400">
                            Notify admin
                          </Badge>
                        )}
                      </div>
                      </div>
                    </div>
                    <div className="flex space-x-2 ml-4">
                      <Button
                        variant="outline"
                        size="icon"
                        onClick={() => handleToggleEnabled(rule)}
                        className="border-gray-700 text-gray-300 hover:bg-gray-800"
                      >
                        {rule.enabled ? (
                          <CheckCircle className="h-4 w-4 text-green-500" />
                        ) : (
                          <XCircle className="h-4 w-4 text-gray-500" />
                        )}
                      </Button>
                      <Button
                        variant="outline"
                        size="icon"
                        onClick={() => handleEditRule(rule)}
                        className="border-gray-700 text-gray-300 hover:bg-gray-800"
                      >
                        <Edit className="h-4 w-4" />
                      </Button>
                      <Button
                        variant="outline"
                        size="icon"
                        onClick={() => handleDeleteRule(rule.id)}
                        className="border-red-500/20 text-red-400 hover:bg-red-500/10"
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))
          )}
        </div>
      </div>
    </div>
  );
}

